import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import SEO from '@/components/SEO';
// Zap dropped with the metric it iconed. BarChart3 and Clock were already
// unused before this change and are left alone.
import { ArrowRight, Check, Target, BarChart3, Clock } from 'lucide-react';
import '@/styles/forms.css';
import '@/styles/landing.css';

// "Onboarding" is gone from this file, in every casing. It stopped being an
// assessable dimension on 2026-07-15 — a flow cannot be audited from a single
// page — and Mobile UX replaced it. This page carried the claim in eight
// places, including its meta description and its JSON-LD, which is why it is
// now covered by backend/tests/test_six_dimensions.py.
//
// What the product actually does on a signup or trial screen: it audits the
// captured page, plus a bounded task-flow crawl of ONE auto-selected task,
// four steps maximum (backend/audit/flow_collector.py). It does not measure
// where trials drop off, which needs analytics we do not have.
const painPoints = [
  { problem: 'Low trial-to-paid conversion', solution: 'Find the friction on your signup and trial screens, with a prioritized list of what to fix first.' },
  { problem: 'Complex, overwhelming dashboards', solution: 'Surface cognitive load issues and visual hierarchy problems that confuse new users.' },
  { problem: 'Feature discovery gaps', solution: 'Pinpoint UI patterns that hide valuable features and reduce perceived product value.' },
  { problem: 'High churn from early friction', solution: 'Catch the micro-frustrations in the first 7 days that lead to cancellations weeks later.' },
];

// The metrics strip is gone entirely. "4x / Faster onboarding" went in 37fc1a0;
// "-63% / Trial drop-off" and "+89% / Trial-to-paid conversion" follow here,
// ruled 2026-08-18. None of the three had a measurement behind it, and there is
// no source to soften them towards, so every rewrite invents a fresh claim.
//
// The same two figures appeared on the deleted DataSync case study and on the
// B2B SaaS card of /use-cases/. One fabrication, three surfaces.

const workflow = [
  { step: '01', title: 'Upload your app screens', desc: 'Share screenshots of your signup screens, dashboard, and key features. No code access needed.' },
  // Title Case: these are the names of the six dimensions, as they appear on
  // every other surface and in server._DIM_TO_FIELD, not a generic word list.
  { step: '02', title: 'AI audits 6 UX dimensions', desc: 'Navigation, Visual Hierarchy, Friction Points, Accessibility, Conversion Design, and Mobile UX.' },
  { step: '03', title: 'Get a sprint roadmap', desc: 'Receive prioritized findings with effort/impact ratings and a three-sprint fix plan your team can action immediately.' },
];

export default function UseCasesSaasPage() {
  const { login, user } = useAuth();

  return (
    <div>
      <SEO
        fullTitle="UX Audit for SaaS Products: Find & Fix Usability Issues Fast"
        description="Run AI-powered UX audits on your SaaS product without hiring an expert. Identify signup friction, navigation issues & conversion blockers. Fix them fast."
        canonical="/use-cases/saas"
        ogTitle="UX Audit for SaaS — Fix Usability Issues | UXAuditPro"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'UX Audit for B2B SaaS Products',
          url: 'https://www.uxauditpro.com/use-cases/saas/',
          description: 'AI-powered UX audits for B2B SaaS companies to reduce churn, improve activation, and boost trial-to-paid conversion.',
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.uxauditpro.com/' },
              { '@type': 'ListItem', position: 2, name: 'Use Cases', item: 'https://www.uxauditpro.com/use-cases/' },
              { '@type': 'ListItem', position: 3, name: 'SaaS', item: 'https://www.uxauditpro.com/use-cases/saas/' },
            ],
          },
        }}
      />

      {/* Hero */}
      <header className="uxa-pt-xl px-6 hero" style={{ position: 'relative', textAlign: 'center' }}>
        <div className="uxa-hero-grid" aria-hidden="true" />
        <div style={{ position: 'relative', maxWidth: 1180, margin: '0 auto' }}>
          <span className="section-label inline-block mb-6">B2B SaaS</span>
          <h1
            className="uxa-h1 mb-6"
            style={{ color: 'var(--ink-1)' }}
          >
            UX audits built for{' '}<br />
            <span style={{ color: 'var(--accent-display)', fontStyle: 'italic', fontWeight: 700 }}>SaaS teams</span>
          </h1>
          <p className="uxa-lead" style={{ color: 'var(--ink-2)', maxWidth: 620, margin: '20px auto 34px' }}>
            Surface signup friction, simplify complex dashboards, and fix the UX friction causing trial users to drop off — before it hits your MRR.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            {user ? (
              <Link to="/audit/new" className="uxa-submit uxa-submit--auto">
                Audit My SaaS App Free
                <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
              </Link>
            ) : (
              <button onClick={login} className="uxa-submit uxa-submit--auto">
                Audit My SaaS App Free
                <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
              </button>
            )}
            <Link to="/use-cases/" className="uxa-submit uxa-submit--auto uxa-submit--secondary">
              All Use Cases
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </header>

      {/* The metrics band was removed on 2026-08-18 — see the note above the
          deleted array near the top of this file. */}

      {/* Pain Points */}
      <section className="uxa-pt-xl">
        <div className="uxa-landing-section">
          <span className="section-label block text-center mb-4">Common SaaS UX Problems</span>
          <h2
            className="uxa-h2 text-center mb-16"
            style={{ color: 'var(--ink-1)' }}
          >
            The UX issues killing your SaaS metrics
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {painPoints.map((item) => (
              <div key={item.problem} className="uxa-feature-card">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: 'var(--accent-tint)' }}>
                    <Target className="w-3.5 h-3.5" style={{ color: 'var(--accent-text)' }} />
                  </div>
                  <h3 className="uxa-h3" style={{ color: 'var(--ink-1)' }}>{item.problem}</h3>
                </div>
                <p className="uxa-dense pl-9" style={{ color: 'var(--ink-2)' }}>{item.solution}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="uxa-pt-xl">
        <div className="uxa-landing-section">
          {/* THE PAGE'S ONE TONAL BREAK. It was a sand band, and the closing
              CTA below it was the dark one -- two breaks, where the system's
              pages take one. Same call as /use-cases/: the break goes where the
              process is, and the CTA becomes the light closing band. */}
          <div className="uxa-dark-panel">
            <div style={{ textAlign: 'center' }}>
              <span className="uxa-dark-panel__eyebrow">How It Works</span>
              <h2 className="uxa-h2" style={{ color: 'var(--text-inverse)', margin: '20px auto 0', maxWidth: 720 }}>
                Audit your SaaS app in 3 steps
              </h2>
            </div>
            <div className="uxa-grid-3" style={{ marginTop: 56, textAlign: 'center' }}>
              {workflow.map((item) => (
                <div key={item.step}>
                  <span style={{ display: 'block', fontSize: 'var(--type-dense-size)', fontWeight: 700, letterSpacing: '0.12em', color: 'var(--accent-soft)', marginBottom: 18 }}>STEP {item.step}</span>
                  <h3 className="uxa-h3" style={{ color: 'var(--text-inverse)', margin: '0 0 8px' }}>{item.title}</h3>
                  <p className="uxa-body" style={{ color: 'var(--ink-4)', margin: 0 }}>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="uxa-pt-xl">
        <div className="uxa-landing-section">
          <span className="section-label block text-center mb-4">What's Included</span>
          <h2
            className="uxa-h2 text-center mb-16"
            style={{ color: 'var(--ink-1)' }}
          >
            Everything your SaaS team needs
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              'Signup and trial screen analysis',
              'Dashboard complexity & cognitive load scoring',
              'Feature discoverability assessment',
              'Accessibility compliance (WCAG 2.1 AA)',
              'Prioritized sprint roadmap (effort × impact)',
              'PDF report ready to share with stakeholders',
              'Business impact estimates with research citations',
              // "Benchmark against SaaS industry averages" removed 2026-08-18.
              // No benchmark corpus exists — the same n=142 problem as the
              // homepage mockup's "vs SaaS avg 65".
              'Severity-ranked findings with the evidence behind each one',
            ].map((item) => (
              <div key={item} className="uxa-feature-card__frame" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <span className="uxa-price-tick" style={{ marginTop: 0, width: 22, height: 22 }} aria-hidden="true"><Check size={12} /></span>
                <span className="text-sm" style={{ color: 'var(--ink-1)' }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      {/* ═══ CLOSING CTA ═══════════════════════════════════════════════════════
          Was an --ink-strong band. The page's one tonal break is now the dark
          How It Works panel above, so this takes the homepage's closing shape:
          sand ground, masked 56px grid, spectrum bloom under the primary
          action. The italic accent moved with the ground -- --accent-soft is
          10.21:1 on ink and --accent-text 2.71:1; on sand it reverses, so the
          value swaps and the pair stays AA. */}
      <section className="uxa-closing" style={{ marginTop: 120 }}>
        <div className="uxa-closing__grid" aria-hidden="true" />
        <div style={{ position: 'relative', maxWidth: 720, margin: '0 auto', textAlign: 'center' }}>
          <h2
            className="uxa-h2 mb-5"
            style={{ color: 'var(--ink-1)' }}
          >
            Fix your SaaS UX today.
          </h2>
          <p
            className="uxa-lead mb-10"
            style={{ color: 'var(--ink-2)', maxWidth: 520, margin: '18px auto 40px' }}
          >
            One free audit every month. No credit card required. Results in under 5 minutes.
          </p>
          <div style={{ position: 'relative', display: 'inline-block', isolation: 'isolate' }}>
            <span style={{ position: 'relative', zIndex: 1, display: 'inline-flex' }}>
            {user ? (
              <Link to="/audit/new" className="uxa-submit uxa-submit--auto">
                Start Free SaaS Audit
                <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
              </Link>
            ) : (
              <button onClick={login} className="uxa-submit uxa-submit--auto">
                Start Free SaaS Audit
                <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
              </button>
            )}
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}
